import Async from "components/DesignSystem/AsyncComponents/Async";
import { Accordion } from "components/DesignSystem/Accordion/Accordion";
import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { FiCheck } from "components/icons/FiCheck";
import { useToast } from "hooks/useToast";
import { useState } from "react";
import { NONE_OF_THESE, REVIEW_ACTION } from "v2/constants/extractionReview";
import { REVIEW_ERROR, STALE_REVIEW_ERRORS } from "v2/constants/reviewError";
import {
  useFinishReviewMutation,
  useGetExtractionReviewQuery,
  usePatchReviewFieldMutation,
} from "v2/store/entityDocumentsApi";
import type {
  ReviewActionFn,
  ReviewFieldAction,
} from "v2/types/extractionReview.types";
import { hasUnsavedReview } from "v2/utils/countReviewFields";
import { readError } from "v2/utils/readError";
import { readErrorCode } from "v2/utils/readErrorCode";
import { ReviewProgressSummary } from "./reviewModal/ReviewProgressSummary";
import { ReviewSectionItem } from "./reviewModal/ReviewSectionItem";

type ReviewExtractedInfoModalProps = {
  entityId: string;
  isOpen: boolean;
  onClose: () => void;

  onConfirmField?: () => void;
  onRejectField?: () => void;
  onFinishReviewClick?: () => void;
};

export const ReviewExtractedInfoModal = ({
  entityId,
  isOpen,
  onClose,
  onConfirmField,
  onRejectField,
  onFinishReviewClick,
}: ReviewExtractedInfoModalProps) => {
  const { alertToast } = useToast();
  const {
    data: review,
    isLoading,
    isError,
    isSuccess,
    refetch,
  } = useGetExtractionReviewQuery({ entityId });
  const [patchField] = usePatchReviewFieldMutation();
  const [finishReview, { isLoading: isFinishing }] = useFinishReviewMutation();
  const [openSections, setOpenSections] = useState<string[]>([]);
  // Field → the action currently awaiting the server, keyed by field_id so two fields
  // can be in-flight at once without one's finally killing the other's spinner.
  const [pending, setPending] = useState<Record<string, ReviewFieldAction>>({});

  const handleOpenChange = (open: boolean): void => {
    if (!open) onClose();
  };

  const runAction = async (
    fieldId: string,
    action: ReviewFieldAction,
    candidateUuid?: string
  ): Promise<void> => {
    // Picking a radio candidate (choose + a real uuid) isn't a decision — no spinner.
    // Only Confirm / Reject / Undo / None-of-these show one.
    const spin =
      action !== REVIEW_ACTION.CHOOSE || candidateUuid === NONE_OF_THESE;
    if (spin) setPending((prev) => ({ ...prev, [fieldId]: action }));
    try {
      await patchField({ entityId, fieldId, action, candidateUuid }).unwrap();
    } catch (error) {
      handleReviewError(error);
    } finally {
      if (spin)
        setPending((prev) => {
          const next = { ...prev };
          delete next[fieldId];
          return next;
        });
    }
  };

  const trackFieldAction = (
    action: ReviewFieldAction,
    candidateUuid?: string
  ): void => {
    const isNoneOfThese =
      action === REVIEW_ACTION.CHOOSE && candidateUuid === NONE_OF_THESE;

    if (action === REVIEW_ACTION.CONFIRM) onConfirmField?.();
    if (action === REVIEW_ACTION.REJECT || isNoneOfThese) onRejectField?.();
  };

  const handleAction: ReviewActionFn = (fieldId, action, candidateUuid) => {
    trackFieldAction(action, candidateUuid);
    void runAction(fieldId, action, candidateUuid);
  };

  const pendingActionFor = (id: string): ReviewFieldAction | null =>
    pending[id] ?? null;

  const handleReviewError = (error: unknown): void => {
    const code = readErrorCode(error);

    if (code && STALE_REVIEW_ERRORS.includes(code)) {
      void refetch();
      return;
    }

    alertToast({ message: readError(error) });
  };

  const isReviewSettled = async (): Promise<boolean> => {
    try {
      const latest = await refetch().unwrap();
      return !hasUnsavedReview(latest);
    } catch {
      return false;
    }
  };

  const handleFinish = async (): Promise<void> => {
    try {
      await finishReview({ entityId }).unwrap();
      onClose();
      return;
    } catch (error) {
      const code = readErrorCode(error);

      const isAlreadyDone =
        code === REVIEW_ERROR.NO_ACTIVE_REVIEW ||
        code === REVIEW_ERROR.NOTHING_TO_CONFIRM;

      if (isAlreadyDone) {
        void refetch();
        onClose();
        return;
      }

      if (await isReviewSettled()) {
        onClose();
        return;
      }

      alertToast({ message: readError(error) });
    }
  };

  const handleFinishClick = (): void => {
    onFinishReviewClick?.();
    void handleFinish();
  };

  const counts = review?.counts;

  const hasFields = (review?.sections ?? []).some(
    (section) => section.fields.length > 0
  );
  // Finish is enabled when there's something to write (confirmed > 0) OR everything has
  // been adjudicated (nothing left to review) — the latter lets a reject-everything /
  // post-partial-finish review close instead of dead-ending with the banner hidden.
  const canFinish =
    (counts?.confirmed ?? 0) > 0 || (counts?.review_remaining ?? 0) === 0;

  return (
    <Modal.Root open={isOpen} onOpenChange={handleOpenChange}>
      <Modal.Content size="large">
        <Modal.Header className="sm:t-pl-4">
          <Modal.Title className="t-mb-0 t-text-title-h2-bold t-text-text-100">
            Review Extracted Information
          </Modal.Title>
          <Modal.Close />
        </Modal.Header>
        <Modal.Body className="sm:t-px-4">
          <Async.Root
            isLoading={isLoading}
            isError={isError}
            isSuccess={isSuccess}
            isEmpty={!review}
          >
            <Async.Empty>
              <div className="t-rounded-xl t-border t-border-solid t-border-neutral-10 t-p-6 t-text-body t-text-neutral-40">
                Nothing to review right now.
              </div>
            </Async.Empty>
            <Async.ErrorHandler>
              <div className="t-rounded-xl t-border t-border-solid t-border-neutral-10 t-p-6 t-text-body t-text-neutral-40">
                Something went wrong loading the extracted information.
              </div>
            </Async.ErrorHandler>
            <Async.Success>
              <ReviewProgressSummary counts={counts} />
              <div className="t-mt-2">
                <Accordion.Root
                  type="multiple"
                  value={openSections}
                  onValueChange={setOpenSections}
                  className="t-flex t-flex-col t-gap-2"
                >
                  {(review?.sections ?? []).map((section) => (
                    <ReviewSectionItem
                      key={String(section.category_group)}
                      section={section}
                      onAction={handleAction}
                      pendingActionFor={pendingActionFor}
                    />
                  ))}
                </Accordion.Root>
              </div>
            </Async.Success>
          </Async.Root>
        </Modal.Body>
        <Modal.FooterButtonGroup>
          <span className="t-mr-auto t-flex t-items-center t-gap-1.5 t-text-body-sm t-text-neutral-50">
            <FiCheck className="t-shrink-0" />
            Your progress is saved as you review
          </span>
          <Button customType="secondary" onClick={onClose}>
            Close
          </Button>
          {hasFields && (
            <Button
              customType="primary"
              onClick={handleFinishClick}
              isLoading={isFinishing}
              disabled={isFinishing || !canFinish}
            >
              Finish review
            </Button>
          )}
        </Modal.FooterButtonGroup>
      </Modal.Content>
    </Modal.Root>
  );
};
